import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import GlobalNavigationShell from "components/v2/global-navigation-shell/GlobalNavigationShell";
import { getLoanCentricGlobalHeaderChrome } from "components/v2/global-header/getLoanCentricGlobalHeaderChrome";
import LoanCentricMobileDock from "components/v2/loan-centric-mobile-dock/LoanCentricMobileDock";
import LoanCentricSidebarNav from "components/v2/loan-centric-sidebar-nav/LoanCentricSidebarNav";
import LoanContentTabBar, { LoanContentTab } from "components/v2/loan-content-tab-bar/LoanContentTabBar";
import SharedNavigation, { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useLoanSidebarData } from "hooks/useLoanSidebarData";
import useTabletChecker from "hooks/useTabletChecker";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveTab,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getProfileSettings,
  getProspectId,
  getTaskCount,
  setProfileActiveTab,
} from "features/authSlice";
import { PalNotificationHandler } from "components/v2/notifications";
import { useNotificationContext } from "contexts/NotificationContext";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useLoanCentricSidebarNavEnabled } from "hooks/useLoanCentricSidebarNavEnabled";
import { resetScrollPosition } from "utilities/resetScrollPosition";
import {
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { trackFSDashboardPageNavigated } from "utilities/full-story";
import { getLoanOfficerPrimaryDomain } from "services/common/loanOfficerSlice";
import classNames from "classnames";
import DashboardTabContent from "../shared-layout/DashboardTabContent";
import { useDashboardShellData } from "./hooks/useDashboardShellData";

interface DashboardShellMainContentProps {
  readonly showNotifications: boolean;
  readonly onCloseNotifications: () => void;
  readonly onDocumentNotificationClick: (palInstanceId: string) => void;
  readonly tabContent: React.ReactNode;
  readonly isDesktop: boolean;
}

/** Renders inside GlobalNavigationShell so NotificationProvider is available. */
const DashboardShellMainContent: React.FC<DashboardShellMainContentProps> = ({
  showNotifications,
  onCloseNotifications,
  onDocumentNotificationClick,
  tabContent,
  isDesktop,
}) => {
  const { notifications, removeNotification } = useNotificationContext();

  const handleMobileNotificationClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    onCloseNotifications();
  };

  if (!isDesktop && showNotifications) {
    return (
      <PalNotificationHandler
        notifications={notifications}
        onNotificationClick={handleMobileNotificationClick}
        onDocumentNotificationClick={onDocumentNotificationClick}
        isOpen={showNotifications}
        onClose={onCloseNotifications}
        triggerElementSelector=".global-header__notification-button"
      />
    );
  }

  return <>{tabContent}</>;
};

const TAB_PARAM_TO_NAV_ID: Record<string, NavigationIds> = {
  overview: NavigationIds.Home,
  home: NavigationIds.Home,
  tasks: NavigationIds.Tasks,
  documents: NavigationIds.Docs,
  "loan-details": NavigationIds.LoanDetails,
};

const NAV_ID_TO_TAB_PARAM: Record<string, string> = {
  [NavigationIds.Home]: "overview",
  [NavigationIds.Tasks]: "tasks",
  [NavigationIds.Docs]: "documents",
  [NavigationIds.LoanDetails]: "loan-details",
};

const resolveCanonicalTabParam = (isValid: boolean, normalizedParam: string | undefined): string => {
  if (!isValid) {
    return "overview";
  }
  if (normalizedParam === "home") {
    return "overview";
  }
  return normalizedParam as string;
};

const resolveMobileNavSlot = (
  isMobilePhone: boolean,
  loanCentricSidebarNavEnabled: boolean,
  loanCentricDock: React.ReactNode,
  sharedNavigation: React.ReactNode,
): React.ReactNode => {
  if (!isMobilePhone) {
    return undefined;
  }
  return loanCentricSidebarNavEnabled ? loanCentricDock : sharedNavigation;
};

export const DashboardShellContent: React.FC = () => {
  const dashboardData = useDashboardShellData();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const isMobilePhone = !isDesktop && !isTablet;
  const { isBannerVisible } = useNativeAppPromoVisibility();
  const dispatch = useDispatch();

  const reduxActiveTab = useSelector(getActiveTab);
  const showProfile = useSelector(getProfileSettings);
  const email = useSelector(getEmailAddress);
  const prospectId = useSelector(getProspectId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const dealId = useSelector(getDealId);
  const taskCount = useSelector(getTaskCount);
  const loPrimaryDomain = useSelector(getLoanOfficerPrimaryDomain);

  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerDelay, setSpinnerDelay] = useState<NodeJS.Timeout | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null);

  const prevShowProfile = useRef(false);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const loanCentricSidebarNavEnabled = useLoanCentricSidebarNavEnabled();
  const { loans } = useLoanSidebarData();
  const loanContextAddress = loans.find((loan) => loan.isActive)?.label ?? "Loan";

  useLayoutEffect(() => {
    resetScrollPosition();
  }, [reduxActiveTab, showProfile]);

  useEffect(() => {
    if (showProfile && !prevShowProfile.current) {
      trackFSDashboardPageNavigated(getNavTrackingProps("Profile"));
    }
    prevShowProfile.current = showProfile;
  }, [showProfile]);

  const hideSpinner = () => {
    clearTimeout(spinnerDelay as NodeJS.Timeout);
    setShowSpinner(false);
  };

  const triggerSpinner = () => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 1000);
    setSpinnerDelay(timeout);
  };

  const getLoSalesDomain = (): string => {
    const domainName = loPrimaryDomain?.domainName?.toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return "Unassigned";
  };

  const getNavTrackingProps = (pageName: string) => ({
    email,
    prospectId,
    sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
    sessionStartTimestamp: sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP),
    pageName,
    loanNumber: loanNumber || null,
    loanGuid: encompassLoanId || loanId || null,
    dealId: dealId || null,
    loSalesDomain: getLoSalesDomain(),
  });

  const PAGE_NAMES: Record<string, string> = {
    [NavigationIds.Home]: "Dashboard Home",
    [NavigationIds.Tasks]: "Tasks",
    [NavigationIds.Docs]: "Documents",
    [NavigationIds.LoanDetails]: "Loan Details",
  };

  const handleTabChange = (tab: string) => {
    if (tab !== NavigationIds.More) {
      triggerSpinner();
    }
    if (tab !== NavigationIds.More && PAGE_NAMES[tab]) {
      trackFSDashboardPageNavigated(getNavTrackingProps(PAGE_NAMES[tab]));
    }
    if (loanCentricSidebarNavEnabled) {
      const tabParam = NAV_ID_TO_TAB_PARAM[tab];
      if (tabParam && searchParams.get("tab") !== tabParam) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", tabParam);
        setSearchParams(newParams);
      }
    }
    dispatch(setProfileActiveTab(tab));
  };

  const handleDocumentNotificationClick = (palInstanceId: string) => {
    if (palInstanceId) {
      setPendingDocumentId(palInstanceId);
      if (loanCentricSidebarNavEnabled && searchParams.get("tab") !== "documents") {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", "documents");
        setSearchParams(newParams);
      }
      dispatch(setProfileActiveTab(NavigationIds.Docs));
    }
  };

  useEffect(() => {
    if (!loanCentricSidebarNavEnabled) {
      return;
    }

    const rawParam = searchParams.get("tab");
    const normalizedParam = rawParam?.toLowerCase();

    let targetNavId = NavigationIds.Home;
    let isValid = false;

    if (normalizedParam && TAB_PARAM_TO_NAV_ID[normalizedParam]) {
      if (normalizedParam !== "loan-details" || featureFlag?.loanDetailsEnabled) {
        targetNavId = TAB_PARAM_TO_NAV_ID[normalizedParam];
        isValid = true;
      }
    }

    const canonicalParam = resolveCanonicalTabParam(isValid, normalizedParam);

    if (!isValid || rawParam !== canonicalParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", canonicalParam);
      setSearchParams(newParams, { replace: true });
    }

    if (reduxActiveTab !== targetNavId) {
      dispatch(setProfileActiveTab(targetNavId));
    }
  }, [
    loanCentricSidebarNavEnabled,
    searchParams,
    setSearchParams,
    featureFlag?.loanDetailsEnabled,
    reduxActiveTab,
    dispatch,
  ]);

  const hideFooter = reduxActiveTab === NavigationIds.More;
  const hideNotifications = reduxActiveTab === NavigationIds.More;
  const headerChrome = getLoanCentricGlobalHeaderChrome({
    enabled: loanCentricSidebarNavEnabled,
    page: "dashboard",
  });
  const headerTitle =
    headerChrome?.headerMode === "loan-context" ? loanContextAddress : headerChrome?.title;

  const tabContent = (
    <DashboardTabContent
      activeTab={reduxActiveTab}
      dashboardData={dashboardData}
      featureFlags={featureFlag}
      pendingDocumentId={pendingDocumentId}
      onLoad={hideSpinner}
      onDocumentOpened={() => setPendingDocumentId(null)}
    />
  );

  const renderSharedNavigation = () => (
    <SharedNavigation
      activeTab={reduxActiveTab}
      onTabChange={handleTabChange}
      isDesktop={isDesktop}
      isTablet={isTablet}
      isNavOpen={false}
      setIsNavOpen={() => undefined}
      loanCentricSidebarNavEnabled={loanCentricSidebarNavEnabled}
    />
  );

  const desktopNavSlot = loanCentricSidebarNavEnabled
    ? <LoanCentricSidebarNav />
    : renderSharedNavigation();

  // [US-07] LoanContentTabBar: dashboard route module supplies tab config (badge
  // count from Dashboard pod's taskCount). Mounts in the FE-02 content header
  // slot for dashboard routes on Desktop + Mobile (AC Scenario 1) whenever
  // loanCentricSidebarNavEnabled is on — gated behind the same epic flag as
  // LoanCentricSidebarNav, at the requester's explicit direction. Sidebar LOAN
  // rows never nest Tasks/Documents links (AC Scenario 3). Application routes
  // never mount this (AC Scenario 4). Content tab bar uses "Overview" for the
  // Home tab per the story description; the sidebar's own Home link keeps its
  // existing "Home" label.
  const contentTabs: LoanContentTab[] = [
    { id: NavigationIds.Home, label: "Overview" },
    { id: NavigationIds.Tasks, label: "Tasks", badge: taskCount > 0 ? taskCount : null },
    { id: NavigationIds.Docs, label: "Documents" },
    ...(featureFlag?.loanDetailsEnabled ? [{ id: NavigationIds.LoanDetails, label: "Loan Details" }] : []),
  ];

  const contentHeaderSlot = loanCentricSidebarNavEnabled ? (
    <LoanContentTabBar tabs={contentTabs} activeTab={reduxActiveTab} onTabChange={handleTabChange} />
  ) : undefined;

  const mobileNavSlot = resolveMobileNavSlot(
    isMobilePhone,
    loanCentricSidebarNavEnabled,
    <LoanCentricMobileDock />,
    renderSharedNavigation(),
  );

  return (
    <div className="dashboard-shell">
      <GlobalNavigationShell
        isLoading={dashboardData.isLoading || showSpinner}
        notificationsEnabled
        shellContext="dashboard"
        shellAnalyticsContext={{
          email,
          prospectId,
          dealId,
          loanNumber: loanNumber || null,
          loanGuid: encompassLoanId || loanId || null,
        }}
        hideFooter={hideFooter}
        hideNotifications={hideNotifications}
        lockBackNavigation={!loanCentricSidebarNavEnabled}
        headerMode={headerChrome?.headerMode}
        headerTitle={headerTitle}
        onNotificationsClick={
          !isDesktop ? () => setShowNotifications(!showNotifications) : undefined
        }
        onDocumentNotificationClick={handleDocumentNotificationClick}
        navSlot={!isMobilePhone ? desktopNavSlot : undefined}
        navSlotPlacement="sidebar"
        contentHeaderSlot={contentHeaderSlot}
        mobileNavSlot={mobileNavSlot}
        mobileContentClassName={classNames({
          "global-navigation-shell__mobile-content--banner-visible": isBannerVisible,
          // ADO-541066 fix scoped behind the flag — see globalNavigationShell.scss.
          "global-navigation-shell__mobile-content--loan-centric-nav": loanCentricSidebarNavEnabled,
        })}
        mobileContainerClassName={
          loanCentricSidebarNavEnabled ? "global-navigation-shell__mobile-container--loan-centric-nav" : undefined
        }
      >
        <DashboardShellMainContent
          showNotifications={showNotifications}
          onCloseNotifications={() => setShowNotifications(false)}
          onDocumentNotificationClick={handleDocumentNotificationClick}
          tabContent={tabContent}
          isDesktop={isDesktop}
        />
      </GlobalNavigationShell>
    </div>
  );
};

export default DashboardShellContent;
